


























































































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { VaultCategory, VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { UploadVaultFile } from "@/types";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseDropZone from "@/components/BaseDropZone";
import BaseIconButton from "@/components/BaseIconButton";
import MediaFileAddButton from "@/components/MediaFileAddButton";
import PageTitle from "@/components/PageTitle";
import { acceptedFiles } from "../../const";
import { VaultUploadStatus } from "../../hooks/useVaultFiles";
import { FilterName, SortType } from "../../types";
import VaultDeleteFilesConfirm from "../VaultDeleteFilesConfirm";
import VaultFilesList from "../VaultFilesList";
import VaultFilterMenu from "../VaultFilterMenu";
import VaultMenu from "../VaultMenu";
import VaultMoveConfirm from "../VaultMoveConfirm";

export default defineComponent({
  name: "VaultManage",
  components: {
    PageTitle,
    VaultMenu,
    BaseDropZone,
    VaultFilesList,
    VaultFilterMenu,
    MediaFileAddButton,
    BaseButton,
    BaseIconButton,
    VaultDeleteFilesConfirm,
    VaultMoveConfirm,
  },
  props: {
    selectedCategory: { type: Object as () => VaultCategory, required: true },
    categories: { type: Array as () => VaultCategory[], required: true },
    files: { type: Array as () => VaultFile[], required: true },
    sortType: { type: String as () => SortType, required: true },
    filterName: { type: String as () => FilterName, default: FilterName.ALL },
    filesLoading: { type: Boolean, default: false },
    fileScroll: { type: Boolean, default: false },
    categoryScroll: { type: Boolean, default: false },
    processing: { type: Boolean, default: false },
    uploadingFiles: { type: Boolean, default: false },
    uploadStatus: {
      type: Object as () => VaultUploadStatus,
      default: () => ({ total: 0, uploaded: 0, skipped: 0, failed: 0 }),
    },
  },
  setup(props, { emit }) {
    const manage = ref(false);
    const checkedFiles = ref<UploadVaultFile[]>([]);
    const showDeletedConfirm = ref(false);
    const showMoveFilesConfirm = ref(false);
    // selection of VaultFilesList, see its onSelect / onClear
    const setSelection = ref<(files: VaultFile[]) => void>(() => {});
    const clearSelection = ref<() => void>(() => {});
    const selectingAll = ref(false);

    const allowUpload = computed(() => !props.selectedCategory.system);
    const categoryList = computed(() => props.categories.filter(c => c.id !== props.selectedCategory.id));
    const noCheckedFiles = computed(() => checkedFiles.value.length === 0);
    const showUnusedOnly = computed(() => props.filterName === FilterName.UNUSED);
    const showUploadStatus = computed(() => props.uploadingFiles || props.uploadStatus.failed > 0);
    const uploadStatusText = computed(() => {
      const { total, uploaded, failed } = props.uploadStatus;
      const parts = [$t("Uploaded {uploaded} of {total}", { uploaded, total })];
      if (failed > 0) parts.push($t("{failed} failed", { failed }));
      return parts.join(" · ");
    });

    const onManage = (value: boolean) => {
      manage.value = value;
      emitter.emit(EVENTS.MAIN_MENU_HIDE, value);
    };

    const onBack = () => {
      if (manage.value) {
        checkedFiles.value = [];
        onManage(false);
        return;
      }
      emit("update:sort", SortType.LAST_USE_TIME_DESC);
      emit("update:filter", FilterName.ALL);
      emit("selectCategoryId", "");
    };

    const resetState = () => {
      onManage(false);
      checkedFiles.value = [];
      showDeletedConfirm.value = false;
      showMoveFilesConfirm.value = false;
    };

    const onMoveFiles = (categoryId: string): void => {
      emit("move:files", { files: checkedFiles.value, categoryId }, resetState);
    };

    // "Select all" / "Deselect all" for moving or deleting a whole folder (CO-15602)
    const selectAll = () => {
      if (selectingAll.value) return;
      selectingAll.value = true;
      emit("load:allFiles", (files: VaultFile[]) => {
        setSelection.value(files);
        selectingAll.value = false;
      });
    };
    const deselectAll = () => clearSelection.value();

    const onDeleteFiles = async () => {
      emit("delete:files", checkedFiles.value, resetState);
    };

    return {
      manage,
      showUnusedOnly,
      categoryList,
      allowUpload,
      acceptedFiles,
      noCheckedFiles,
      showUploadStatus,
      uploadStatusText,
      showMoveFilesConfirm,
      showDeletedConfirm,
      checkedFiles,
      setSelection,
      clearSelection,
      selectingAll,
      selectAll,
      deselectAll,
      onMoveFiles,
      onDeleteFiles,
      onManage,
      onBack,
    };
  },
});
